import { getArmorClass, getPassiveScore } from '@character-creator/core';
import type { SkillName } from '@character-creator/types';
import type { CharacterBenefits } from '../../../hooks/useCharacterBenefits';
import type { PlaySession } from '../../../stores/playSessionStore';
import { ABILITY_LABELS, SKILLS } from './constants';

interface CombatStatsColumnProps {
  benefits: CharacterBenefits;
  session: PlaySession | null;
  onRollAbility: (label: string, modifier: number) => void;
  getSkillMod: (skill: SkillName) => {
    mod: number;
    isProficient: boolean;
    hasExpertise: boolean;
  };
}

export function CombatStatsColumn({
  benefits,
  session,
  onRollAbility,
  getSkillMod,
}: CombatStatsColumnProps) {
  const currentHp = session?.currentHp ?? benefits.hitPoints;
  const maxHp = session?.maxHp ?? benefits.hitPoints;
  const tempHp = session?.tempHp ?? 0;

  return (
    <div className="space-y-4">
      {/* Combat Stats Row */}
      <div className="grid grid-cols-4 gap-2">
        {/* AC (Base/Unarmored) */}
        <div className="bg-background-card border border-background-elevated rounded-lg p-3 text-center">
          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1">
            Armor Class
          </div>
          <div className="font-mono text-2xl font-bold text-accent-gold">
            {getArmorClass(benefits)}
          </div>
          <div className="text-[9px] text-text-muted">unarmored</div>
        </div>

        {/* Initiative */}
        <button
          onClick={() => onRollAbility('Initiative', benefits.modifiers.dexterity)}
          className="bg-background-card border border-background-elevated rounded-lg p-3 text-center
            hover:border-accent-bronze/30 transition-colors"
        >
          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1">
            Initiative
          </div>
          <div className="font-mono text-2xl font-bold text-text-primary">
            {benefits.modifiers.dexterity >= 0 ? '+' : ''}{benefits.modifiers.dexterity}
          </div>
        </button>

        {/* Speed */}
        <div className="bg-background-card border border-background-elevated rounded-lg p-3 text-center">
          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1">Speed</div>
          <div className="font-mono text-2xl font-bold text-text-primary">
            {benefits.speed}
            <span className="text-sm text-text-muted"> ft</span>
          </div>
        </div>

        {/* HP */}
        <div className="bg-background-card border border-background-elevated rounded-lg p-3 text-center">
          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1">
            Hit Points
          </div>
          <div className="font-mono text-xl font-bold text-success">
            {currentHp}
            <span className="text-text-muted">/{maxHp}</span>
          </div>
          {tempHp > 0 && (
            <div className="text-xs text-class-wizard mt-1">+{tempHp} temp</div>
          )}
        </div>
      </div>

      {/* Skills */}
      <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
        <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
          Skills
        </div>
        <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-1">
          {SKILLS.map(({ name, ability, skillId }) => {
            const { mod, isProficient, hasExpertise } = getSkillMod(skillId);

            return (
              <button
                key={name}
                onClick={() => onRollAbility(name, mod)}
                className="flex items-center gap-2 py-1.5 hover:bg-background-elevated/50 rounded px-2 -mx-2 transition-colors"
              >
                <div
                  className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                    hasExpertise
                      ? 'bg-accent-gold border-accent-gold ring-2 ring-accent-gold/30'
                      : isProficient
                        ? 'bg-accent-gold border-accent-gold'
                        : 'border-text-muted'
                  }`}
                />
                <span className="flex-1 text-sm text-text-secondary text-left">{name}</span>
                <span className="text-[10px] text-text-muted uppercase">{ABILITY_LABELS[ability]}</span>
                <span
                  className={`font-mono text-sm font-bold min-w-[28px] text-right ${
                    isProficient ? 'text-accent-gold' : 'text-text-primary'
                  }`}
                >
                  {mod >= 0 ? '+' : ''}{mod}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Passive Scores */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-background-card border border-background-elevated rounded-lg p-2 text-center">
          <div className="text-[9px] text-text-muted uppercase tracking-wider">Passive Perception</div>
          <div className="font-mono text-lg font-bold text-text-primary">
            {getPassiveScore(benefits, 'perception')}
          </div>
        </div>
        <div className="bg-background-card border border-background-elevated rounded-lg p-2 text-center">
          <div className="text-[9px] text-text-muted uppercase tracking-wider">Passive Investigation</div>
          <div className="font-mono text-lg font-bold text-text-primary">
            {getPassiveScore(benefits, 'investigation')}
          </div>
        </div>
        <div className="bg-background-card border border-background-elevated rounded-lg p-2 text-center">
          <div className="text-[9px] text-text-muted uppercase tracking-wider">Passive Insight</div>
          <div className="font-mono text-lg font-bold text-text-primary">
            {getPassiveScore(benefits, 'insight')}
          </div>
        </div>
        {benefits.spellcasting && (
          <div className="bg-background-card border border-background-elevated rounded-lg p-2 text-center">
            <div className="text-[9px] text-text-muted uppercase tracking-wider">Spell Save DC</div>
            <div className="font-mono text-lg font-bold text-class-warlock">
              {benefits.spellcasting.saveDC}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
